import React from "react";

const share = (v: number) =>
  `${Math.round(Math.min(Math.max(v, 0), 1) * 10000) / 100}%`;
const whole = (v: number) => `${Math.round(v * 100)}%`;

/** One usage limit (a plan's 5-hour or weekly window): a solid bar for what
 * was used and a hatched one out to where it would have been without help. */
export function LimitMeter({
  label,
  used,
  without,
  scale,
  resets,
  lastReported = false,
  reading,
}: {
  label: string;
  /** Share of the limit used, 0 to 1. */
  used: number | null;
  /** The same share without condense; may pass 1. */
  without?: number | null;
  /** Shared upper bound, in multiples of a limit, for a group of meters. */
  scale?: number;
  resets?: string;
  lastReported?: boolean;
  reading?: string;
}) {
  const value = used ?? 0;
  const comparisonValue = lastReported || used == null ? null : without;
  const over = comparisonValue != null && comparisonValue >= 1;
  const full = !lastReported && used != null && value >= 1;
  const kept =
    comparisonValue != null && comparisonValue > value
      ? comparisonValue - value
      : 0;
  const extent = Math.max(1, scale ?? 1, value, comparisonValue ?? 0);
  const tone = full ? " bad" : over ? " warn" : "";
  const status = full
    ? "Limit reached"
    : over
      ? "Without condense you'd have hit this limit"
      : null;
  const comparison =
    comparisonValue != null
      ? `≈${whole(comparisonValue)} without condense`
      : null;
  const saved =
    kept > 0
      ? `${kept < 0.005 ? "<1%" : `≈${whole(kept)}`} of window saved`
      : null;
  const amount =
    used == null
      ? "Usage unknown"
      : lastReported
        ? `Last reported: ${whole(value)}`
        : `${whole(value)} used`;
  return (
    <div
      className={`ds-limit${tone}`}
      role="img"
      aria-label={[`${label}: ${amount}`, status, comparison, saved, reading]
        .filter(Boolean)
        .join(", ")}
    >
      <div className="ds-limit-head">
        <span className="ds-limit-label">{label}</span>
        {resets && <span className="ds-limit-reset">{resets}</span>}
      </div>
      <div className="ds-limit-bar">
        <span
          className="ds-limit-used"
          style={{ width: share(value / extent) }}
        />
        {kept > 0 && (
          <span
            className="ds-limit-kept"
            style={{
              left: share(value / extent),
              width: share(kept / extent),
            }}
          />
        )}
        <i
          className="ds-limit-mark"
          style={{ left: share(1 / extent) }}
          aria-hidden
        />
      </div>
      <div className="ds-limit-foot">
        <strong>{amount}</strong>
        {status && <span className="ds-limit-said">{status}</span>}
        {comparison && <span>{comparison}</span>}
        {saved && <span className="ds-limit-saved">{saved}</span>}
        {reading && <span>{reading}</span>}
      </div>
    </div>
  );
}

/** The meters' three marks, said once under a group of them. */
export function LimitKey() {
  return (
    <div className="ds-limit-key" aria-hidden>
      <span>
        <i className="used" />
        Used
      </span>
      <span>
        <i className="kept" />
        Saved by condense
      </span>
      <span>
        <i className="limit" />
        100% limit
      </span>
    </div>
  );
}
